import React, { useState, useEffect, useContext, useMemo } from "react";
import { Grid } from "rsuite";
import { toast } from "react-toastify";
import { PropertyStoreContext } from "../../../contexts/PropertyStoreContext";
import { getRoomTypes } from "../../../api";
import PropertyConfigHeader from "../PropertyConfigHeader";
import PropertyPageCard from "../PropertyPageCard";
import PropertyConfigTable from "../PropertyConfigTable";
import { getNextSortDirection } from "../tableUtils";
import { DEFAULT_PAGE_SIZE } from "../tableConstants";
import {
  filterRoomTypesBySearch,
  sortRoomTypesByRoom,
  sortRoomTypesByStatus,
  formatOccupancyRange,
} from "./utils";
import "../propertyConfig.scss";
import "./roomTypes.scss";

const getColumns = (
  roomSortDirection,
  statusSortDirection,
  onRoomSort,
  onStatusSort
) => [
  {
    key: "room",
    label: "Room",
    className: "room-types-page__col-room",
    headerClassName: "room-types-page__col-room-header",
    sortable: true,
    sortDirection: roomSortDirection,
    onSort: onRoomSort,
    render: (row) => `${row.code || "-"} | ${row.displayName || "-"}`,
  },
  {
    key: "isActive",
    label: "Status",
    className: "room-types-page__col-status",
    headerClassName: "room-types-page__col-status-header",
    sortable: true,
    sortDirection: statusSortDirection,
    onSort: onStatusSort,
    render: (row) => (
      <span
        className={`room-types-page__status-badge${
          !row.isActive ? " room-types-page__status-badge--inactive" : ""
        }`}
      >
        {row.isActive ? "Active" : "Inactive"}
      </span>
    ),
  },
  {
    key: "adultOccupancy",
    label: "Min | Max Adult",
    className: "room-types-page__col-adult",
    render: (row) =>
      formatOccupancyRange(row.minAdultOccupancy, row.maxAdultOccupancy),
  },
  {
    key: "childOccupancy",
    label: "Min | Max Children",
    className: "room-types-page__col-children",
    render: (row) =>
      formatOccupancyRange(row.minChildOccupancy, row.maxChildOccupancy),
  },
  {
    key: "totalMaxOccupancy",
    label: "Max Total",
    className: "room-types-page__col-max-total",
    render: (row) =>
      row.totalMaxOccupancy != null ? row.totalMaxOccupancy : "-",
  },
];

const RoomTypes = () => {
  const { selectedProperty } = useContext(PropertyStoreContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roomSortDirection, setRoomSortDirection] = useState(null);
  const [statusSortDirection, setStatusSortDirection] = useState(null);

  useEffect(() => {
    if (!selectedProperty?.propertyId) return;

    let cancelled = false;
    setRoomTypes([]);
    setLoading(true);
    setSearchQuery("");
    setRoomSortDirection(null);
    setStatusSortDirection(null);

    getRoomTypes(selectedProperty.propertyId)
      .then((response) => {
        if (cancelled) return;
        const entity = response?.data?.data?.getEntityWithRelations;
        if (
          entity?.success === false ||
          (entity?.errors && entity.errors.length > 0)
        ) {
          toast.error("Failed to load room types. Please try again later.");
        } else {
          const roomTypeRelation = entity?.relations?.find(
            (r) => r.entityType === "ROOMTYPE"
          );
          setRoomTypes(roomTypeRelation?.entities || entity?.entity || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("Failed to load room types. Please try again later.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedProperty?.propertyId]);

  const handleRoomSortToggle = () => {
    setRoomSortDirection((prev) => getNextSortDirection(prev));
    setStatusSortDirection(null);
  };

  const handleStatusSortToggle = () => {
    setStatusSortDirection((prev) => getNextSortDirection(prev));
    setRoomSortDirection(null);
  };

  const processedRoomTypes = useMemo(() => {
    const filtered = filterRoomTypesBySearch(roomTypes, searchQuery);
    if (roomSortDirection)
      return sortRoomTypesByRoom(filtered, roomSortDirection);
    if (statusSortDirection)
      return sortRoomTypesByStatus(filtered, statusSortDirection);
    return filtered;
  }, [roomTypes, searchQuery, roomSortDirection, statusSortDirection]);

  return (
    <Grid fluid className="property-config-page">
      <PropertyConfigHeader title="Room Types" />

      <PropertyPageCard
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchAriaLabel="Search room types"
      >
        <PropertyConfigTable
          columns={getColumns(
            roomSortDirection,
            statusSortDirection,
            handleRoomSortToggle,
            handleStatusSortToggle
          )}
          data={processedRoomTypes}
          rowKey="id"
          loading={loading}
          loadingMessage="Loading room types..."
          emptyMessage="No room types found."
          pagination="client"
          pageSize={DEFAULT_PAGE_SIZE}
        />
      </PropertyPageCard>
    </Grid>
  );
};

export default RoomTypes;
